import { useState } from 'react'

import { useField } from 'formik'
import {
  EmployeeSelectorInitialValuesDocument,
  UniversalExportsExporterFilterKindEnum,
  UniversalExportsExporterFilterOption,
} from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import { datesToLocalString } from 'modules/expenses/lib/common'
import { FormFilter } from 'modules/universal_exports/components/ExportSidepanel/types'

interface FilterSelectorProps {
  name: string
  kind: UniversalExportsExporterFilterKindEnum
  label: string
  options: UniversalExportsExporterFilterOption[]
}

const FilterSelector: React.FC<FilterSelectorProps> = ({ name, kind, label, options }) => {
  const [_, { value: filter }, { setValue }] = useField<FormFilter>(name)
  const [employees, setEmployees] = useState<EmployeeForSelectFragmentNoAccess[] | undefined>()
  const { company } = useCurrentSession()

  const onChange = (values?: string[]) => {
    setValue({ ...filter, values: values ?? [] })
  }

  const onChangeDate = (values?: string[]) => {
    if (values) {
      // We set the date range to include the beginning_of_day for the start date (00:00 LOCAL TIME)
      // up until the end_of_day for the end date (23:59 LOCAL TIME)
      const { from, to } = datesToLocalString(values[0], values[1])

      onChange(compact([from, to]))
    } else {
      onChange(values)
    }
  }

  // Set initial employees if filter is for employees
  useQuery(EmployeeSelectorInitialValuesDocument, {
    variables: {
      companyId: company.id,
      ids: filter.values.map(Number),
    },
    skip: kind != 'employees' || employees !== undefined, // if employees is not undefined means this query has already done, so not need to repeat.
    onCompleted: (data) => {
      const employeeFragments = data.employees.employeeNames
      if (kind == 'employees' && employeeFragments) {
        setEmployees(employeeFragments)
      }
    },
  })

  const onEmployeeChange = (newEmployees?: EmployeeForSelectFragmentNoAccess[]) => {
    setEmployees(newEmployees ?? [])
    setValue({ ...filter, values: (newEmployees ?? []).map((emp) => emp.id + '') })
  }

  switch (kind) {
    case 'multiplechoice':
      return (
        <SelectOrCreate.Popover
          mandatory={false}
          label={label}
          placeholder=""
          onValuesChange={onChange}
          values={filter.values}
          isMultiSelect
          onChange={() => null}
        >
          {options.map((option) => {
            return (
              <SelectOrCreate.Option
                key={option.value}
                label={option.label}
                value={option.value}
                searchValue={option.label}
              >
                {option.label}
              </SelectOrCreate.Option>
            )
          })}
        </SelectOrCreate.Popover>
      )
    case 'employees':
      return (
        <EmployeeSelector
          multiple
          onSelect={onEmployeeChange}
          label={label}
          includeInactive
          emptyLabel=""
          hideSearchIcon
          value={employees ?? []}
        />
      )
    case 'singlechoice':
      return (
        <SelectOrCreate.Popover
          mandatory={false}
          label={label}
          placeholder=""
          onValuesChange={onChange}
          values={filter.values}
          isMultiSelect={false}
          onChange={() => null}
        >
          {options.map((option) => {
            return (
              <SelectOrCreate.Option
                key={option.value}
                label={option.label}
                value={option.value}
                searchValue={option.label}
              >
                {option.label}
              </SelectOrCreate.Option>
            )
          })}
        </SelectOrCreate.Popover>
      )
    case 'datetimerange':
      const [from, to] = filter.values

      const fromDate = from ? dateToString(moment(from), 'backend') : undefined
      const toDate = to ? dateToString(moment(to), 'backend') : undefined

      return (
        <DayPicker
          mode="range"
          format="ui"
          valueFormat="backend"
          value={[fromDate, toDate]}
          label={label}
          closeOnChange={false}
          onChange={(values) => onChangeDate(compact(values))}
          maxDate={new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 3)}
        />
      )
    default:
      return null
  }
}

export default FilterSelector
